import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Row,
  Select,
  Space,
  Typography,
  message,
} from 'antd'
import { Download, FileSpreadsheet, RotateCcw } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import type { Dayjs } from 'dayjs'
import { MovementTable } from '@/components/journey/MovementTable'
import { PageContainer } from '@/components/common/PageContainer'
import { PageHeader } from '@/components/common/PageHeader'
import { SectionCard } from '@/components/common/SectionCard'
import { PageEmpty, PageError, PageLoading } from '@/components/common/PageStates'
import {
  reportsService,
  type ReportSelectOption,
  type RfidReportFilters,
} from '@/services/reports.service'
import { engineService } from '@/services/engine.service'
import { useRegionStore } from '@/stores/regionStore'
import { useAuthStore } from '@/stores/authStore'
import { canAccessRfidEngineAdmin } from '@/utils/rfidAccess'

const { RangePicker } = DatePicker
const { Text } = Typography

const FALLBACK_TRANSFER_TYPES = [
  'LEAPOUT',
  'LEAPIN',
  'CUSTOUT',
  'CUSTIN',
  'TROUT',
  'TRIN',
  'RELOUT',
  'RELIN',
]

type FormState = {
  locations: string[]
  range: [Dayjs, Dayjs] | null
  materialCode: string | undefined
  materialGroupCode: string | undefined
  materialGroupName: string | undefined
  transferType: string | undefined
  document: string | undefined
  kitNo: string | undefined
  inTransit: 'yes' | 'no' | undefined
}

const EMPTY: FormState = {
  locations: [],
  range: null,
  materialCode: undefined,
  materialGroupCode: undefined,
  materialGroupName: undefined,
  transferType: undefined,
  document: undefined,
  kitNo: undefined,
  inTransit: undefined,
}

function toSelectOptions(
  values: Array<string | ReportSelectOption> | undefined
): ReportSelectOption[] {
  if (!values?.length) return []
  return values.map((v) => (typeof v === 'string' ? { value: v, label: v } : v))
}

function buildFilters(form: FormState): RfidReportFilters {
  return {
    locations: form.locations.length ? form.locations : undefined,
    created_at_from: form.range?.[0]?.format('YYYY-MM-DD'),
    created_at_to: form.range?.[1]?.format('YYYY-MM-DD'),
    material_code: form.materialCode || undefined,
    material_group_code: form.materialGroupCode || undefined,
    material_group_name: form.materialGroupName || undefined,
    transfer_type: form.transferType || undefined,
    document: form.document || undefined,
    kit_no: form.kitNo || undefined,
    in_transit: form.inTransit || undefined,
  }
}

function hasAnyFilter(filters: RfidReportFilters): boolean {
  return Object.values(filters).some((v) => {
    if (v == null) return false
    if (Array.isArray(v)) return v.length > 0
    return String(v).trim().length > 0
  })
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  mode,
  loading,
}: {
  label: string
  value: string | string[] | undefined
  onChange: (v: any) => void
  options: ReportSelectOption[]
  placeholder: string
  mode?: 'multiple' | 'tags'
  loading?: boolean
}) {
  return (
    <Form.Item label={label}>
      <Select
        className="w-full"
        allowClear
        showSearch
        mode={mode}
        loading={loading}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        options={options}
        maxTagCount="responsive"
        filterOption={(input, option) => {
          if (!option) return false
          const label = String(option.label ?? '').toLowerCase()
          const val = String(option.value ?? '').toLowerCase()
          const needle = input.toLowerCase().trim()
          if (label.includes(needle) || val.includes(needle)) return true
          const unpaddedNeedle = needle.replace(/^0+/, '')
          if (unpaddedNeedle) {
            const unpaddedLabel = label.replace(/^0+/, '')
            const unpaddedVal = val.replace(/^0+/, '')
            if (unpaddedLabel.includes(unpaddedNeedle) || unpaddedVal.includes(unpaddedNeedle)) {
              return true
            }
          }
          return false
        }}
      />
    </Form.Item>
  )
}

export default function ReportsPage() {
  const [form, setForm] = useState<FormState>(EMPTY)
  const [applied, setApplied] = useState<RfidReportFilters>({})
  const [downloading, setDownloading] = useState(false)
  const [tagReportDownloading, setTagReportDownloading] = useState(false)
  const [lastFile, setLastFile] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const user = useAuthStore((s) => s.user)
  const showTagMappingReport = canAccessRfidEngineAdmin(user)

  const filters = useMemo(() => buildFilters(form), [form])
  const canDownload = hasAnyFilter(filters)
  const regionView = useRegionStore((s) => s.view)

  const optionsQ = useQuery({
    queryKey: ['rfid-report-options', regionView],
    queryFn: () => reportsService.getOptions(),
    staleTime: 5 * 60_000,
  })

  const previewQ = useQuery({
    queryKey: ['rfid-report-preview', regionView, applied],
    queryFn: () => reportsService.preview(applied),
  })

  const patch = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setError(null)
  }

  const transferOptions = toSelectOptions(
    optionsQ.data?.transfer_types?.length
      ? optionsQ.data.transfer_types
      : FALLBACK_TRANSFER_TYPES
  )
  const kitOptions = toSelectOptions(optionsQ.data?.kit_nos)
  const documentOptions = toSelectOptions(optionsQ.data?.documents)
  const materialOptions = optionsQ.data?.material_codes ?? []
  const materialGroupCodeOptions = optionsQ.data?.material_group_codes ?? []
  const materialGroupNameOptions = toSelectOptions(optionsQ.data?.material_group_names)

  const locationOptions = useMemo(() => {
    const map = new Map<string, string>()
    const add = (v?: string | ReportSelectOption) => {
      if (!v) return
      const val = typeof v === 'string' ? v.trim() : v.value?.trim()
      const lbl = typeof v === 'string' ? v.trim() : (v.label?.trim() || v.value?.trim())
      if (!val) return
      if (!map.has(val)) {
        map.set(val, lbl || val)
      } else if (lbl && lbl !== val && map.get(val) === val) {
        map.set(val, lbl)
      }
    }
    for (const l of optionsQ.data?.locations || []) add(l)
    for (const s of optionsQ.data?.senders || []) add(s)
    for (const r of optionsQ.data?.receivers || []) add(r)
    for (const c of optionsQ.data?.customer_locations || []) add(c)
    for (const w of optionsQ.data?.warehouses || []) add(w)
    for (const c2 of optionsQ.data?.c2_locations || []) add(c2)
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [optionsQ.data])

  const applyPreview = () => {
    if (Boolean(form.range?.[0]) !== Boolean(form.range?.[1])) {
      setError('Date range needs both start and end dates.')
      return
    }
    setError(null)
    setApplied({ ...filters })
  }

  const onDownload = async () => {
    if (!canDownload) {
      setError('Add at least one filter before downloading.')
      return
    }
    if (Boolean(form.range?.[0]) !== Boolean(form.range?.[1])) {
      setError('Date range needs both start and end dates.')
      return
    }

    setDownloading(true)
    setError(null)
    try {
      setApplied({ ...filters })
      const { filename } = await reportsService.downloadRfidReport(filters)
      setLastFile(filename)
      message.success(`Downloaded ${filename}`)
    } catch (e) {
      const msg = (e as { message?: string })?.message || 'Report download failed'
      setError(msg)
      message.error(msg)
    } finally {
      setDownloading(false)
    }
  }

  const onDownloadTagMapping = async () => {
    setTagReportDownloading(true)
    setError(null)
    try {
      const { filename, count } = await engineService.downloadTagMappingReport({})
      setLastFile(filename)
      message.success(`Downloaded ${count} tag mapping row${count === 1 ? '' : 's'}`)
    } catch (e) {
      const msg = (e as { message?: string })?.message || 'Tag mapping report download failed'
      setError(msg)
      message.error(msg)
    } finally {
      setTagReportDownloading(false)
    }
  }

  const optsLoading = optionsQ.isLoading

  return (
    <PageContainer fluid>
      <PageHeader
        title="RFID Reports"
        subtitle="Preview loads immediately (latest 20). Add filters to refine, then download Excel."
      />

      {error ? (
        <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />
      ) : null}

      {optionsQ.isError ? (
        <Alert
          type="warning"
          showIcon
          message="Could not load filter dropdowns from the server. Transfer types still available."
        />
      ) : null}

      {lastFile ? (
        <Alert
          type="success"
          showIcon
          icon={<FileSpreadsheet className="h-4 w-4" />}
          message={`Last download: ${lastFile}`}
        />
      ) : null}

      {showTagMappingReport ? (
        <SectionCard
          className="mb-5"
          title="RFID tag mapping (Engine)"
          subtitle="All birthed/tagged RFIDs with SKU mapping status from the engine registry."
        >
          <Space wrap>
            <Button
              type="primary"
              icon={<Download className="h-4 w-4" />}
              loading={tagReportDownloading}
              onClick={() => void onDownloadTagMapping()}
            >
              Download tag mapping report
            </Button>
            <Link to="/admin/rfid-tags">
              <Button type="default">Open tags master (filter & remap)</Button>
            </Link>
          </Space>
          <Typography.Paragraph type="secondary" className="mb-0 mt-3 text-xs">
            CSV columns include RFID code, status (birthed/tagged), whether a SKU is mapped, SKU
            code, country, serial, and birth/tag metadata. Opens in Excel.
          </Typography.Paragraph>
        </SectionCard>
      ) : null}

      <SectionCard
        title="Movement report filters"
        subtitle="Filter by location (sender, receiver, customer, plant), date range, and movement details."
      >
        <Form layout="vertical" className="report-filters">
          <Row gutter={[16, 8]}>
            <Col xs={24} lg={12}>
              <FilterSelect
                label="Location"
                mode="tags"
                loading={optsLoading}
                placeholder="Select or enter location, plant, customer, sender or receiver"
                value={form.locations}
                onChange={(v) => patch('locations', v ?? [])}
                options={locationOptions}
              />
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="Date range (created at)">
                <RangePicker
                  className="w-full"
                  value={form.range}
                  onChange={(v) => patch('range', (v as [Dayjs, Dayjs] | null) ?? null)}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12} xl={8}>
              <FilterSelect
                label="SKU (material code)"
                loading={optsLoading}
                placeholder="Select SKU"
                value={form.materialCode}
                onChange={(v) => patch('materialCode', v)}
                options={materialOptions}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <FilterSelect
                label="Material group code"
                loading={optsLoading}
                placeholder="Select material group code"
                value={form.materialGroupCode}
                onChange={(v) => patch('materialGroupCode', v)}
                options={materialGroupCodeOptions}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <FilterSelect
                label="Material group name"
                loading={optsLoading}
                placeholder="Select material group name"
                value={form.materialGroupName}
                onChange={(v) => patch('materialGroupName', v)}
                options={materialGroupNameOptions}
              />
            </Col>

            <Col xs={24} md={12} xl={8}>
              <FilterSelect
                label="Transfer type"
                loading={optsLoading}
                placeholder="Select transfer type"
                value={form.transferType}
                onChange={(v) => patch('transferType', v)}
                options={transferOptions}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <FilterSelect
                label="Document"
                loading={optsLoading}
                placeholder="Select document"
                value={form.document}
                onChange={(v) => patch('document', v)}
                options={documentOptions}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <FilterSelect
                label="Kit number"
                loading={optsLoading}
                placeholder="Select kit number"
                value={form.kitNo}
                onChange={(v) => patch('kitNo', v)}
                options={kitOptions}
              />
            </Col>

            <Col xs={24} md={12} xl={8}>
              <FilterSelect
                label="In Transit"
                placeholder="Yes = draft / No = posted"
                value={form.inTransit}
                onChange={(v) => patch('inTransit', v)}
                options={[
                  { value: 'yes', label: 'Yes (draft / In Transit = X)' },
                  { value: 'no', label: 'No (normal posted)' },
                ]}
              />
            </Col>
          </Row>

          <div className="mt-2 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
            <Text type="secondary" className="text-xs">
              Preview works with or without filters (latest 20 rows). Download needs at least one
              filter.
            </Text>
            <Space wrap>
              <Button
                icon={<RotateCcw className="h-4 w-4" />}
                onClick={() => {
                  setForm(EMPTY)
                  setApplied({})
                  setError(null)
                  setLastFile(null)
                }}
              >
                Reset
              </Button>
              <Button onClick={applyPreview} loading={previewQ.isFetching}>
                Preview
              </Button>
              <Button
                type="primary"
                icon={<Download className="h-4 w-4" />}
                loading={downloading}
                disabled={!canDownload}
                onClick={() => void onDownload()}
              >
                Download Excel
              </Button>
            </Space>
          </div>
        </Form>
      </SectionCard>

      {previewQ.isLoading ? (
        <SectionCard title="Preview">
          <PageLoading />
        </SectionCard>
      ) : previewQ.isError ? (
        <PageError
          message={(previewQ.error as { message?: string })?.message || 'Preview failed'}
        />
      ) : (
        <SectionCard
          title="Preview"
          subtitle={`Showing ${Math.min(previewQ.data?.data.length ?? 0, previewQ.data?.previewLimit ?? 20)} of ${previewQ.data?.total ?? 0} matching movements`}
          count={previewQ.data?.total}
          variant="table"
        >
          {(previewQ.data?.data.length ?? 0) === 0 ? (
            <PageEmpty description="No movements match the current filters." />
          ) : (
            <MovementTable rows={previewQ.data?.data ?? []} loading={previewQ.isFetching} />
          )}
        </SectionCard>
      )}
    </PageContainer>
  )
}
